import { Alert, Button, Col, Form, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import DynamicForm from "../controls/DynamicForm";
import InModal from "../controls/InModal";
import { CompanyAsset } from "../repository/data.default";
import { QueryOperation } from "../code/search-query/search-query";
import {
  addCompanyAsset,
  deleteCompanyAsset,
  getAssetSubtypes,
  getAssetTypes,
  getCompanyAsset,
  updateCompanyAsset,
} from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";
import { CompanyAssetForm } from "./CompanyAssetView.default";

export interface CompanyAssetViewProps {
  readonly uid?: string | null;
  readonly companyUid: string;
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly onFinish?: () => void;
  readonly onClose?: () => void;
}

function CompanyAssetView(props: CompanyAssetViewProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<CompanyAssetForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Something went wrong, try again");
  const [item, setItem] = useState<CompanyAsset | null>(null);

  const assetTypeRequest = async () => {
    const { data } = await getAssetTypes({
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: 200,
    });
    return data.map((t) => ({ display: t.name, value: t.uid }));
  };

  // Subtype options cascade off the chosen Asset Type (and reset when it changes).
  const assetSubtypeRequest = async () => {
    const typeUid = (form.getFieldValue("assetType") as any)?.value as
      | string
      | undefined;
    if (!typeUid) return [];
    const { data } = await getAssetSubtypes({
      searchQuery: {
        conditions: [
          { field: "assetTypeUid", operation: QueryOperation.Equal, values: [typeUid] },
        ],
      },
      pageNumber: 0,
      pageSize: 200,
    });
    return data.map((s) => ({ display: s.name, value: s.uid }));
  };

  useEffect(() => {
    if (props.open) loadData();
    else reset();
  }, [props.open, props.uid]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((v) => setValid(!!Object.values(v).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function loadData(): void {
    if ((props.mode !== "update" && props.mode !== "delete") || !props.uid) {
      reset();
      return;
    }
    setLoading(true);
    setError(false);
    getCompanyAsset(props.uid)
      .then((a) => {
        setLoading(false);
        setItem(a);
        form.setFieldsValue({
          name: a.name,
          code: a.code,
          assetType: a.assetTypeUid
            ? { display: a.assetTypeName, value: a.assetTypeUid }
            : undefined,
          assetSubtype: a.assetSubtypeUid
            ? { display: a.assetSubtypeName, value: a.assetSubtypeUid }
            : undefined,
        });
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }

  function reset(): void {
    setValid(false);
    setError(false);
    setLoading(false);
    setItem(null);
    if (props.open) form.resetFields();
  }

  function done(): void {
    setLoading(false);
    props.onFinish?.();
  }

  function fail(e: any): void {
    setErrorMessage(getProblemMessage(e));
    setLoading(false);
    setError(true);
  }

  function onSubmit(): void {
    setLoading(true);
    setError(false);

    if (props.mode === "delete") {
      deleteCompanyAsset(props.uid!).then(done).catch(fail);
      return;
    }

    const { name, code, assetType, assetSubtype } = values;
    const payload = {
      name,
      code,
      assetTypeUid: (assetType as any)?.value,
      assetSubtypeUid: (assetSubtype as any)?.value,
      companyUid: props.companyUid,
    };

    if (props.mode === "add") {
      addCompanyAsset(payload as any).then(done).catch(fail);
    } else {
      updateCompanyAsset({
        uid: item?.uid,
        rowVersion: item?.rowVersion,
        ...payload,
      } as any)
        .then(done)
        .catch(fail);
    }
  }

  const title =
    (props.mode === "add" ? "Add" : props.mode === "update" ? "Edit" : "Delete") +
    " Asset";

  return (
    <InModal
      open={props.open}
      title={title}
      size="small"
      onCancel={() => props.onClose?.()}
      actions={
        <Space className="sticky bottom-0 flex flex-row items-center justify-end">
          <Button disabled={loading} onClick={() => props.onClose?.()}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger={props.mode === "delete"}
            loading={loading}
            disabled={props.mode !== "delete" && !valid}
            onClick={onSubmit}
          >
            {props.mode === "add" ? "Add" : props.mode === "update" ? "Save" : "Delete"}
          </Button>
        </Space>
      }
    >
      <Col className="flex flex-col">
        {props.mode !== "delete" && (
          <DynamicForm
            className="w-auto"
            form={form}
            schema={{
              groups: [
                {
                  controls: [
                    {
                      type: "textbox",
                      name: "name",
                      label: "Name",
                      rules: [{ required: true, message: "Name is required" }],
                    },
                    {
                      type: "textbox",
                      name: "code",
                      label: "Code",
                    },
                    {
                      type: "combobox",
                      name: "assetType",
                      label: "Asset Type",
                      request: assetTypeRequest,
                      rules: [{ required: true, message: "Asset type is required" }],
                    },
                    {
                      type: "combobox",
                      name: "assetSubtype",
                      label: "Subtype",
                      // Cascades off Asset Type — resets when the type changes.
                      dependsOn: ["assetType"],
                      request: assetSubtypeRequest,
                    },
                  ],
                },
              ],
            }}
          />
        )}
        {props.mode === "delete" && (
          <Typography.Paragraph>
            <Typography.Text>
              Continue deleting the{" "}
              <Typography.Text className="font-bold">{item?.name}</Typography.Text> asset?
            </Typography.Text>
          </Typography.Paragraph>
        )}
        {error && <Alert className="mb-2" message={errorMessage} type="error" />}
      </Col>
    </InModal>
  );
}

export default CompanyAssetView;
